import React, { useEffect } from "react";
import { Users, Code, Briefcase, MessageCircle, Brain } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VideoEmbed } from "../ui/VideoEmbed/VideoEmbed";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import codamWriteYourFuture from "../../../assets/videos/codam-write-your-future.mp4";

import images from "../../../constants/images";

import "./CodamMentoring.scss";

const CodamMentoring = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletListLeft = (
    <BulletList>
      <BulletItem>Real-world engineering practices</BulletItem>
      <BulletItem>Career decision-making and positioning</BulletItem>
      <BulletItem>Understanding industry expectations beyond code</BulletItem>
      <BulletItem>Navigating technical specialisation choices</BulletItem>
      <BulletItem>
        Balancing technical depth with business and product awareness
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Mentoring Software Engineers Through the Codam Fellows Program"
        subtitle="Supporting the next generation of engineers with real-world technical and career guidance"
        category="MENTORING"
        role="Mentor — Codam Fellows Program"
        platform="Education, In-person Workshops & Mentorship"
        technologies={[
          "JavaScript (ES6+)",
          " React.js",
          " Node.js",
          " Python",
          " AI & Machine Learning fundamentals",
          " Git",
          " REST APIs",
          " Software architecture",
        ]}
        year="2022 - 2023"
        client="Codam Coding College"
      />
      <VisualShowcase
        layout="full"
        imageAlt="Codam Coding College"
        imageUrl={images.codamNightTime}
      />
      <ProjectOverview
        challengeContent={[
          "Bridging the gap between education and real-world engineering careers",
          "While Codam students develop strong technical foundations, many face challenges when translating those skills into:",
        ]}
        extraChallengeContent={[
          "The challenge was to provide practical, personalised mentorship that helped students think beyond coursework and prepare for sustainable careers in software engineering.",
        ]}
        solutionContent={[
          "Long-term, hands-on mentorship grounded in industry experience",
          "As part of the 2022–2023 Codam Fellows Program, I joined as a mentor to work closely with students over an extended period.",
          "Rather than one-off sessions, the focus was on ongoing mentorship, adapting guidance to each student’s interests, strengths, and career goals. The mentorship blended technical direction with broader professional development, ensuring students could connect their learning to real industry contexts.",
        ]}
        metrics={[
          {
            value: "8+ weeks",
            label: "Intensive professional development mentorship",
          },
          {
            value: "2 domains",
            label: "Web engineering & AI-focused development",
          },
          {
            value: "Industry-aligned",
            label: "Mentorship grounded in real-world engineering practice",
          },
          {
            value: "Career-ready",
            label: "Focus on interviews, teamwork, and market positioning",
          },
          {
            value: "Practical",
            label: "100% discussion- and project-driven sessions",
          },
        ]}
        bulletsLeft={bulletListLeft}
      />
      <VideoEmbed
        src={codamWriteYourFuture}
        controls={true}
        poster={images.codam}
        title="Codam — “Write Your Future”"
        description="This video provides background on Codam’s learning philosophy and the environment in which I supported students through long-term, industry-led mentorship as part of the Codam Fellows Program."
      />
      <KeyFeatures
        title="Practical, Industry-Focused Mentorship"
        description="Focused on preparing students for real-world software engineering careers, beyond purely technical skills."
        features={[
          {
            icon: Users,
            title: "1:1 technical mentorship",
            description:
              "Direct mentoring tailored to each student’s focus area, including web development and AI-oriented engineering.",
          },
          {
            icon: Code,
            title: "Real-world engineering practices",
            description:
              "Guidance on writing maintainable code, architectural thinking, and professional development workflows.",
          },
          {
            icon: Briefcase,
            title: "Career and market readiness",
            description:
              "Support on navigating the job market, interviews, contracts, and understanding industry dynamics.",
          },
          {
            icon: MessageCircle,
            title: "Confidence and communication",
            description:
              "Helping students articulate their value, collaborate effectively, and operate confidently within teams.",
          },
          {
            icon: Brain,
            title: "Business-minded engineering",
            description:
              "Encouraging product thinking and commercial awareness alongside technical decision-making.",
          },
        ]}
      />
      <ProcessSection
        title="Developing Engineers Through Practice and Perspective"
        description="A mentorship-led approach combining technical coaching, professional insight, and real-world context."
        phases={[
          {
            number: "01",
            title: "Understanding Goals & Skill Gaps",
            description:
              "Worked with students to understand their technical interests, strengths, and career ambitions.",
            deliverables: [
              "Skill and interest alignment",
              "Career direction discussions",
              "Mentorship focus areas",
            ],
          },
          {
            number: "02",
            title: "Hands-on Technical Coaching",
            description:
              "Provided practical guidance through code discussions, architectural reasoning, and problem-solving sessions.",
            deliverables: [
              "Framework and language guidance",
              "Code quality and structure feedback",
              "Real-world engineering examples",
            ],
          },
          {
            number: "03",
            title: "Professional Development & Mindset",
            description:
              "Covered non-technical skills required to thrive in the industry, from interviews to team dynamics.",
            deliverables: [
              "Interview preparation",
              "Market and salary discussions",
              "Imposter syndrome reframing",
            ],
          },
          {
            number: "04",
            title: "Long-term Career Readiness",
            description:
              "Focused on setting students up for sustainable growth beyond Codam.",
            deliverables: [
              "Career planning guidance",
              "Learning strategies",
              "Confidence building",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactLabel="Project Impact"
        impactTitle="Preparing engineers to thrive beyond graduation"
        impactDescription="Through sustained mentorship, students gained clearer direction in their technical paths, stronger confidence in their abilities, and a more realistic understanding of the software engineering profession. The project reinforced Codam’s mission of producing engineers who are not only technically capable, but adaptable, thoughtful, and industry-ready."
        nextProject={{
          title: "Agile Project Management",
          link: "/project/agile",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Great engineers are shaped through context, not just code",
          description:
            "This project highlighted the importance of mentorship in technical education. Real growth happens when students can contextualise their skills within industry realities — understanding not just how to build software, but why, for whom, and at what scale. It reaffirmed my belief that the strongest engineers are business-aware, product-focused, and continuously learning, regardless of their technical specialisation.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { CodamMentoring };

/*
Project Context

Codam is a tuition-free software engineering university built on a peer-to-peer learning model with no traditional teachers or lectures. Students learn through intensive, project-based work that mirrors real professional engineering environments.

Their education model is 100% practical, 100% project-based, and 100% collaborative, designed to prepare students not just to code, but to operate effectively in modern software teams and industries.

To complement this approach, Codam runs the Fellows Program, connecting experienced industry professionals with students for longer-term mentorship.


My Role

Mentor — Codam Fellows Program

I mentored two students throughout the duration of the program, each with a distinct technical focus:

Student 1: Web application development

JavaScript

React.js

Node.js

Application architecture and best practices

Student 2: Python & Artificial Intelligence

Python fundamentals and advanced usage

AI concepts and practical applications

Problem-solving approaches in data-driven systems

Responsibilities included:

Providing regular technical guidance and feedback

Helping students identify and deepen their technical strengths

Advising on career paths, roles, and industry expectations

Encouraging product thinking and business awareness

Supporting confidence, autonomy, and professional growth
*/
